import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AtlantaSoftwareDevelopmentPage = () => {
  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Custom Software Development in Atlanta, GA',
    url: 'https://thealphaconsult.com/atlanta-software-development',
    serviceType: 'Custom software development',
    provider: {
      '@type': 'ProfessionalService',
      name: 'Alpha Consulting LLC',
      image: 'https://thealphaconsult.com/logo.png',
      telephone: '+1-770-545-1557',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Atlanta',
        addressRegion: 'GA',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'City',
      name: 'Atlanta'
    },
    description: 'Custom software development services for Atlanta businesses, startups, and enterprise teams.'
  };

  return (
    <div className="pt-24">
      <SEO
        title="Custom Software Development in Atlanta, GA | Alpha Consulting LLC"
        description="Atlanta custom software development for startups, SMBs, and enterprise teams. We design and build web apps, internal platforms, SaaS products, APIs, and automation tools."
        keywords="custom software development Atlanta GA, Atlanta software developers, software company Atlanta, SaaS development Atlanta, web app development Atlanta, API development Atlanta"
        url="https://thealphaconsult.com/atlanta-software-development"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
        structuredData={pageSchema}
      />

      <section className="relative py-20 bg-gradient-to-b from-black to-orange-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-wider text-amber-300/90 font-nunito">
              Atlanta software development
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl tracking-tight font-nunito font-bold">
              Custom software development for Atlanta businesses
            </h1>
            <p className="mt-6 text-lg text-orange-300 font-nunito">
              Alpha Consulting LLC helps Atlanta startups, operations teams, and growing companies build modern software that improves customer experience, unlocks efficiency, and supports revenue growth.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-xl font-semibold font-nunito">What we build</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                SaaS products, customer portals, internal operations tools, API integrations, dashboards, and workflow automation.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-xl font-semibold font-nunito">Who we support</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Founders launching new products, established Atlanta businesses modernizing systems, and enterprise teams extending delivery capacity.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h2 className="text-xl font-semibold font-nunito">How we deliver</h2>
              <p className="mt-3 text-orange-300 font-nunito">
                Product strategy, UX, engineering, QA, and cloud delivery in one team with clear milestones and measurable outcomes.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-black/40 p-8">
            <h2 className="text-2xl font-semibold font-nunito">Why Atlanta companies work with us</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <p className="text-orange-300 font-nunito">
                Local businesses often need more than a generic dev shop. They need a technology partner who can translate business goals into a product roadmap, choose a practical architecture, and deliver without adding operational drag.
              </p>
              <p className="text-orange-300 font-nunito">
                We focus on software that solves real business problems: replacing manual workflows, connecting disconnected systems, launching new digital products, and improving visibility through dashboards and reporting.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-orange-900/40 p-8">
            <h2 className="text-2xl font-semibold font-nunito">Frequently asked questions</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-lg font-semibold font-nunito text-amber-300">Do you work with Atlanta startups?</h3>
                <p className="mt-2 text-orange-300 font-nunito">
                  Yes. We help founders validate ideas, ship MVPs, and build production-ready SaaS platforms with room to scale.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold font-nunito text-amber-300">Can you modernize an existing system?</h3>
                <p className="mt-2 text-orange-300 font-nunito">
                  Yes. We can incrementally modernize legacy systems, improve integrations, and reduce operational bottlenecks without requiring a full rewrite on day one.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold font-nunito text-amber-300">Do you support internal business software?</h3>
                <p className="mt-2 text-orange-300 font-nunito">
                  Absolutely. We build internal tools, admin portals, reporting systems, and operational workflows that support finance, sales, logistics, and service teams.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold font-nunito text-amber-300">Do you only work in Georgia?</h3>
                <p className="mt-2 text-orange-300 font-nunito">
                  We are based around Atlanta and serve Georgia businesses, but we also work remotely with teams across the United States.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-100 text-black hover:bg-orange-200 transition-all hover:scale-[1.02] font-nunito font-medium"
            >
              Talk with our Atlanta team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AtlantaSoftwareDevelopmentPage;
